"use client";

import React, { useState, useEffect, useRef, useCallback } from "react";
import "./App.css";
import "./ProfileStyles.css"; // ✅ ADD THIS LINE
import rigbyHead from "./rigby-head.png";
import { auth, provider, signInWithPopup } from "./firebase";
import { onAuthStateChanged, signOut, signInWithEmailAndPassword, sendEmailVerification, sendPasswordResetEmail } from 'firebase/auth';
import SignupPage from "./SignupPage";
import ChatScreen from "./chat/ChatScreen";
import HistoryView from "./history/HistoryView";
import ProfilePage from "./profile/ProfilePage";
import TasksScreen from "./tasks/TasksScreen";
import LockInScreen from "./lockin/LockInScreen";
import GuestEnded from "./guest/GuestEnded";
import { FerpaInfoModal, FerpaConsentModal, TermsModal, CrisisModal, MessageModal } from "./modals/LegalModals";
import { breakDownMessage, MAX_TASK_LENGTH } from "./tasks/taskSummary";
import { signInMethodOf, memberSinceLabel } from "./profile/profileFormat";
import { greeting } from "./chat/copy";
import { DialogProvider, notify, confirmDialog } from "./dialogs";
import { NoticeDialog, ForgotPasswordDialog } from "./AuthDialogs";
import AuthActionPage from "./AuthActionPage";
import { consentCopy, hasPendingConsent, clearPendingConsent, isUnverifiedPasswordUser, requestVerificationEmailDetailed, requestPasswordReset, requestPasswordResetDetailed } from "./authUtils";

// API Base URL
// Uses relative URL in Docker (nginx proxies to backend). For Render/other hosts, set REACT_APP_API_URL.
const API_BASE = process.env.REACT_APP_API_URL || "";

const IconGoogle = () => (
  <svg width="18" height="18" viewBox="0 0 48 48">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3a12 12 0 11-3.3-13l5.7-5.7A20 20 0 1044 24c0-1.2-.1-2.4-.4-3.5z" />
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8A12 12 0 0124 16c3 0 5.7 1.1 7.8 3l5.7-5.7A20 20 0 006.3 14.7z" />
    <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2A12 12 0 0112.7 28l-6.5 5A20 20 0 0024 44z" />
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3a12 12 0 01-4.1 5.6l6.2 5.2c-.4.4 6.6-4.8 6.6-14.8 0-1.2-.1-2.4-.4-3.5z" />
  </svg>
);

const IconArrow = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h14M13 5l7 7-7 7" />
  </svg>
);

const IconLock = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" />
    <path d="M7 11V7a5 5 0 0110 0v4" />
  </svg>
);

const IconCheck = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 6L9 17l-5-5" />
  </svg>
);

const IconEye = ({ open = true }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    {open ? (
      <>
        <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
        <circle cx="12" cy="12" r="3" />
      </>
    ) : (
      <>
        <path d="M3 3l18 18" />
        <path d="M10.5 10.7a2 2 0 002.8 2.8" />
        <path d="M9.9 5.1A10.4 10.4 0 0112 5c6.5 0 10 7 10 7a16 16 0 01-3.5 4.4M6.6 6.6C3.6 8.5 2 12 2 12s3.5 7 10 7c1.6 0 3.1-.4 4.4-1" />
      </>
    )}
  </svg>
);

function App() {
  const [isGuest, setIsGuest] = useState(false);
  // Guest demo limits are enforced by the server; these only drive the countdown and end screen
  const [guestExpiresAt, setGuestExpiresAt] = useState(null);
  const [guestSecondsLeft, setGuestSecondsLeft] = useState(null);
  const [guestEnded, setGuestEnded] = useState(null); // null | "time" | "messages"
  // Branded dialogs for the sign-in screen (replace browser prompt/alert popups)
  const [forgotOpen, setForgotOpen] = useState(false);
  const [authNotice, setAuthNotice] = useState(null); // null | { title, body, primary?, secondary? }
  // Branded notice shown when a guest session can't start (replaces browser alerts)
  const [guestNotice, setGuestNotice] = useState(null); // null | { title, body, signup }
  const [isRegister, setIsRegister] = useState(false);
  // Links emailed by Firebase carry ?mode=verifyEmail|resetPassword&oobCode=...
  const [authAction, setAuthAction] = useState(() => {
    const p = new URLSearchParams(window.location.search);
    const mode = p.get("mode");
    const oobCode = p.get("oobCode");
    return oobCode && (mode === "verifyEmail" || mode === "resetPassword") ? { mode, oobCode } : null;
  });
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [darkMode, setDarkMode] = useState(false);
  const [showLockIn, setShowLockIn] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [showCrisisModal, setShowCrisisModal] = useState(false);
  const chatBoxRef = useRef(null);

  // File upload states
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [showUploadMenu, setShowUploadMenu] = useState(false);
  const fileInputRef = useRef(null);

  // Sidebar & User states
  const [userName, setUserName] = useState("Guest User");
  const [userFirstName, setUserFirstName] = useState("");
  const [userAvatar, setUserAvatar] = useState(null);
  const [userId, setUserId] = useState(null); // Store user ID for user-specific tasks
  const [showTaskPage, setShowTaskPage] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [newTask, setNewTask] = useState("");
  const avatarInputRef = useRef(null);
  // When true, auth state listener should skip calling backend (handleGoogleSignIn will do it)
  const googleSignInInProgressRef = useRef(false);

  // Conversation memory state
  const [currentConversationId, setCurrentConversationId] = useState(null);
  
  // Total message count across all conversations
  const [totalMessageCount, setTotalMessageCount] = useState(0);
  
  // Firebase user state (kept for potential future use)
  // eslint-disable-next-line no-unused-vars
  const [firebaseUser, setFirebaseUser] = useState(null);
  
  // Auth loading state
  const [authLoading, setAuthLoading] = useState(false);
  
  // Login form state
  const [loginUsername, setLoginUsername] = useState("");
  const [loginPassword, setLoginPassword] = useState("");

  // Modal states
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showFerpaModal, setShowFerpaModal] = useState(false);
  // FERPA consent required after Google sign-in (backend returned 403)
  const [showFerpaConsentForGoogle, setShowFerpaConsentForGoogle] = useState(false);
  const [pendingGoogleUser, setPendingGoogleUser] = useState(null);

  const [showProfilePage, setShowProfilePage] = useState(false); // ✅ NEW
  
  // Conversations sidebar state
  const [showConversations, setShowConversations] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [conversationsLoading, setConversationsLoading] = useState(false);
  
  // Password visibility toggles
  const [showLoginPassword, setShowLoginPassword] = useState(false);

  /* ===================================================== */
  /* ============== PROFILE DATA HANDLERS ================ */
  /* ===================================================== */
  const exportUserData = async () => {
    try {
      // Export data from backend (database)
      const response = await fetch(`${API_BASE}/export-data`, {
        method: "GET",
        credentials: "include",
      });

      let backendData = null;
      if (response.ok) {
        const backendResponse = await response.json();
        backendData = backendResponse.data;
      }

      // Combine backend data with local state data
      const userData = {
        userName,
        userFirstName,
        userAvatar,
        // Current messages in state (may not be all conversations)
        currentMessages: messages,
        tasks,
        preferences: {
          darkMode
        },
        // Backend database data (all conversations and messages)
        databaseData: backendData,
        exportDate: new Date().toISOString(),
        appVersion: "1.0.0",
        trademark: `Rigby™ Emotional Support Bot - © ${new Date().getFullYear()} My Emotional Support Bot`,
        note: backendData 
          ? "This export includes all your conversations and messages from the database."
          : "Note: Could not retrieve database data. This export only includes current session data."
      };

      const dataStr = JSON.stringify(userData, null, 2);
      const dataBlob = new Blob([dataStr], { type: "application/json" });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `rigby-data-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      notify("✅ Your data has been exported successfully!");
    } catch (error) {
      console.error("Export error:", error);
      notify("⚠️ Error exporting data. Please try again.");
    }
  };

  const deleteUserData = async () => {
    const confirmed = await confirmDialog({
      title: "Delete all your data?",
      body:
        "This permanently deletes:\n\n" +
        "\u2022 All chat history and conversations\n" +
        "\u2022 Tasks\n" +
        "\u2022 User preferences\n" +
        "\u2022 Profile information\n\n" +
        "Your account stays active, and you can log in and start fresh.\n\n" +
        "This cannot be undone.",
      confirmLabel: "Continue",
      danger: true,
    });

    if (confirmed) {
      const doubleCheck = await confirmDialog({
        title: "Final confirmation",
        body: "Are you 100% sure you want to delete all your data? This is your last chance to cancel.",
        confirmLabel: "Delete all my data",
        danger: true,
      });

      if (doubleCheck) {
        try {
          // Delete data from backend database
          const response = await fetch(`${API_BASE}/delete-all-data`, {
            method: "DELETE",
            credentials: "include",
          });

          if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(errorData?.detail || "Failed to delete data from server");
          }

          // Clear all local data
          localStorage.clear();
          setMessages([]);
          setTasks([]);
          setCurrentConversationId(null);
          setTotalMessageCount(0);
          setUserName("Guest User");
          setUserFirstName("");
          setUserAvatar(null);
          setDarkMode(false);
          
          notify("✅ All your data has been permanently deleted.\n\nYour account is still active. You can log in again to start fresh.");
          handleLogout();
        } catch (error) {
          console.error("Delete error:", error);
          notify(`⚠️ Error deleting data: ${error.message}\n\nLocal data has been cleared, but server data may still exist.`);
          
          // Still clear local data even if server deletion failed
          localStorage.clear();
          setMessages([]);
          setTasks([]);
          setCurrentConversationId(null);
        }
      }
    }
  };

  // Deletes the account and everything with it. The Profile page asks for confirmation first.
  const deleteAccountNow = async () => {
    try {
      const response = await fetch(`${API_BASE}/delete-account`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.detail || "Failed to delete account from server");
      }
      localStorage.clear();
      setMessages([]);
      setTasks([]);
      setCurrentConversationId(null);
      setTotalMessageCount(0);
      setUserName("Guest User");
      setUserFirstName("");
      setUserAvatar(null);
      setDarkMode(false);
      notify("Your account has been permanently deleted.\n\nYou will be redirected to the login page.", { title: "Account deleted" });
      handleLogout();
    } catch (error) {
      console.error("Delete account error:", error);
      notify(`Error deleting account: ${error.message}\n\nPlease contact support if this issue persists.`);
    }
  };

  // Sends the signed-in student a password reset link (email accounts only).
  const handleChangePassword = async () => {
    const email = auth.currentUser && auth.currentUser.email;
    if (!email) return;
    let sent = await requestPasswordReset(email);
    if (!sent) {
      try {
        await sendPasswordResetEmail(auth, email);
        sent = true;
      } catch (_) {
        sent = false;
      }
    }
    notify(
      sent
        ? `We sent a link to ${email}. Check your inbox and spam folder.`
        : "We couldn't send the email right now. Please try again in a moment.",
      { title: sent ? "Check your email" : "Something went wrong" }
    );
  };

  /* ===================================================== */
  /* ================== PREFERENCES ====================== */
  /* ===================================================== */
  
  // Prevent guests from accessing profile page
  useEffect(() => {
    if ((isGuest || !isAuthenticated) && showProfilePage) {
      setShowProfilePage(false);
    }
  }, [isGuest, isAuthenticated, showProfilePage]);
  
  // Function to get the tasks storage key based on user type
  // Use useCallback to memoize the function and include it in useEffect dependencies
  const getTasksStorageKey = useCallback(() => {
    if (userId) {
      return `rigby-tasks-user-${userId}`;
    }
    return "rigby-tasks-guest";
  }, [userId]);

  // Load tasks when userId changes (for authenticated users) or on initial load (for guests)
  useEffect(() => {
    const tasksKey = getTasksStorageKey();
    const savedTasks = localStorage.getItem(tasksKey);
    if (savedTasks) {
      try {
        setTasks(JSON.parse(savedTasks));
      } catch (e) {
        console.error("Error parsing saved tasks:", e);
        setTasks([]);
      }
    } else {
      setTasks([]);
    }
  }, [userId, getTasksStorageKey]); // Reload tasks when userId changes

  useEffect(() => {
    const savedMode = localStorage.getItem("rigby-dark-mode");
    if (savedMode) setDarkMode(savedMode === "true");
    // Tasks are now loaded in the useEffect above based on userId
  }, []);

  useEffect(() => localStorage.setItem("rigby-dark-mode", darkMode), [darkMode]);
  // Don't save userName to localStorage - let authentication handle it
  // This prevents stale names from interfering with fresh logins
  
  // Save tasks to user-specific or guest storage
  useEffect(() => {
    const tasksKey = getTasksStorageKey();
    localStorage.setItem(tasksKey, JSON.stringify(tasks));
  }, [tasks, userId, getTasksStorageKey]);
  useEffect(() => {
    // Only save avatar to localStorage if it's a valid URL
    // Don't save null or empty strings
    if (userAvatar && userAvatar.trim() !== "") {
      localStorage.setItem("rigby-user-avatar", userAvatar);
    } else {
      // Remove from localStorage when avatar is cleared
      localStorage.removeItem("rigby-user-avatar");
    }
  }, [userAvatar]);

  /* ===================================================== */
  /* ========== CHECK SESSION ON MOUNT =================== */
  /* ===================================================== */
  const fetchTotalMessageCount = async () => {
    try {
      const response = await fetch(`${API_BASE}/message-count`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setTotalMessageCount(data.total_messages || 0);
      }
    } catch (error) {
      console.error("Error fetching message count:", error);
    }
  };

  const loadConversations = async () => {
    setConversationsLoading(true);
    try {
      const response = await fetch(`${API_BASE}/conversations`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setConversations(data.conversations || []);
      } else {
        console.error("Failed to load conversations");
      }
    } catch (error) {
      console.error("Error loading conversations:", error);
    } finally {
      setConversationsLoading(false);
    }
  };

  const loadConversation = async (conversationId) => {
    try {
      const response = await fetch(`${API_BASE}/conversations/${conversationId}`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        const loadedMessages = data.messages.map(msg => ({
          sender: msg.sender,
          text: msg.message,
          timestamp: msg.timestamp
        }));
        setMessages(loadedMessages);
        setCurrentConversationId(conversationId);
        setShowConversations(false);
        
        // Scroll to bottom
        setTimeout(() => {
          if (chatBoxRef.current) {
            chatBoxRef.current.scrollTo({
              top: chatBoxRef.current.scrollHeight,
              behavior: "smooth"
            });
          }
        }, 100);
      } else {
        notify("Failed to load conversation");
      }
    } catch (error) {
      console.error("Error loading conversation:", error);
      notify("Error loading conversation");
    }
  };

  // Deletes a chat for good. The History view calls this once the Undo window has passed.
  const deleteConversationById = async (conversationId) => {
    try {
      const response = await fetch(`${API_BASE}/conversations/${conversationId}?hard_delete=true`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!response.ok) {
        notify("Couldn't delete that chat. Please try again.");
        return false;
      }
      setConversations((prev) => prev.filter((conv) => conv.id !== conversationId));
      if (currentConversationId === conversationId) {
        setCurrentConversationId(null);
        setMessages([
          {
            sender: "bot",
            text: greeting(userFirstName || "friend"),
            timestamp: new Date().toISOString(),
          },
        ]);
      }
      fetchTotalMessageCount();
      return true;
    } catch (error) {
      console.error("Error deleting conversation:", error);
      notify("Couldn't delete that chat. Please try again.");
      return false;
    }
  };

  const checkSession = async () => {
    // Returns true when a signed-in session already exists
    try {
      const response = await fetch(`${API_BASE}/auth/me`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        if (data.user.is_guest) {
          // A reloaded guest session stays a guest (with its remaining time), not a signed-in user
          setUserName("Guest");
          setUserFirstName("Guest");
          setIsGuest(true);
          setGuestExpiresAt(Date.now() + (data.user.expires_in_seconds || 0) * 1000);
          setGuestEnded(null);
          setMessages([
            {
              sender: "bot",
              text: "Welcome back! I'm here to listen and support you. What's on your mind?",
              timestamp: new Date().toISOString(),
            }
          ]);
          return true;
        }
        const firstName = data.user.first_name || data.user.username?.split('@')[0] || "User";
        const lastName = data.user.last_name || "";
        // Use full name for display, or first name if last name is not available
        const fullName = lastName ? `${firstName} ${lastName}` : firstName;
        setUserName(fullName);
        setUserFirstName(firstName);
        setIsGuest(false); // User is authenticated, not a guest
        setIsAuthenticated(true); // Mark as authenticated
        
        // Store user ID for user-specific tasks
        if (data.user.id || data.user.user_id) {
          setUserId(data.user.id || data.user.user_id);
        }
        
        // Load avatar from backend if available
        if (data.user.avatar_url) {
          setUserAvatar(data.user.avatar_url);
        }
        
        // Fetch total message count
        fetchTotalMessageCount();
        
        // Load conversations
        loadConversations();
        
        // Rigby welcome message
        setMessages([
          {
            sender: "bot",
            text: greeting(firstName),
            timestamp: new Date().toISOString(),
          }
        ]);
        return true;
      }
    } catch (error) {
      console.log("No active session");
    }
    return false;
  };

  useEffect(() => {
    (async () => {
      const hasSession = await checkSession();
      // Website "Try the live demo" links to /?guest=1 and should land straight in a guest chat
      const params = new URLSearchParams(window.location.search);
      if (params.get("guest") === "1") {
        window.history.replaceState({}, "", window.location.pathname);
        if (!hasSession) handleGuest();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ===================================================== */
  /* ============ FIREBASE AUTH LISTENER ================= */
  /* ===================================================== */
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setFirebaseUser(user);
        // If sign-in was triggered by the button, only handleGoogleSignIn calls the backend
        if (googleSignInInProgressRef.current) {
          return;
        }
        // Email/password users must verify their email before the backend will accept them
        if (isUnverifiedPasswordUser(user)) {
          return;
        }
        try {
          const idToken = await user.getIdToken(true);
          const response = await fetch(`${API_BASE}/auth/firebase-login`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({
              idToken: idToken,
              email: user.email,
              displayName: user.displayName,
              photoURL: user.photoURL
            }),
          });

          if (response.status === 403 && response.headers.get("X-Requires-FERPA-Consent") === "true") {
            // Consent was already given on the signup form (same browser): record it now
            if (hasPendingConsent(user.email)) {
              clearPendingConsent(user.email);
              await doFirebaseConsentLogin(user);
              return;
            }
            setShowFerpaConsentForGoogle(true);
            setPendingGoogleUser(user);
            return;
          }
          if (response.ok) {
            const data = await response.json();
            const firstName = data.user?.first_name || user.displayName?.split(' ')[0] || "friend";
            const lastName = data.user?.last_name || user.displayName?.split(' ').slice(1).join(' ') || "";
            // Use full name for display, or first name if last name is not available
            const fullName = lastName ? `${firstName} ${lastName}` : firstName;
            
            setUserName(fullName);
            setUserFirstName(firstName);
            
            // Handle avatar: use Firebase photoURL if available
            // Check photoURL from multiple sources to be sure
            const photoURL = user.photoURL || user.providerData?.[0]?.photoURL;
            console.log("Firebase auth listener - photoURL from user.photoURL:", user.photoURL);
            console.log("Firebase auth listener - photoURL from providerData:", user.providerData?.[0]?.photoURL);
            console.log("Firebase auth listener - Final photoURL:", photoURL);
            console.log("Firebase auth listener - photoURL type:", typeof photoURL);
            console.log("Firebase auth listener - Full user object keys:", Object.keys(user));
            
            if (photoURL && typeof photoURL === 'string' && photoURL.trim() !== "" && photoURL !== "null" && photoURL !== "undefined") {
              const trimmedURL = photoURL.trim();
              console.log("✅ Setting avatar from Firebase photoURL:", trimmedURL);
              // Set the avatar immediately - use a function to ensure state update
              setUserAvatar(trimmedURL);
            } else {
              console.warn("⚠️ No valid photoURL available, showing initials");
              setUserAvatar(null); // Explicitly set to null to show initials
              // Clear any cached avatar from localStorage
              localStorage.removeItem("rigby-user-avatar");
            }
            setIsGuest(false); // Google sign-in users are authenticated, not guests
            setIsAuthenticated(true); // Mark as authenticated
            
            // Store user ID for user-specific tasks
            if (data.user?.id) {
              setUserId(data.user.id);
            }
            
            // Load avatar from backend if available (backend avatar takes precedence over Google photoURL)
            if (data.user?.avatar_url) {
              setUserAvatar(data.user.avatar_url);
            } else if (photoURL && typeof photoURL === 'string' && photoURL.trim() !== "" && photoURL !== "null" && photoURL !== "undefined") {
              // Fall back to Google photoURL if no backend avatar
              const trimmedURL = photoURL.trim();
              setUserAvatar(trimmedURL);
            }
            
            // Fetch total message count after Firebase login
            fetchTotalMessageCount();
            
            // Rigby welcome message for Google sign-in
            setMessages([
              {
                sender: "bot",
                text: greeting(firstName),
                timestamp: new Date().toISOString(),
              }
            ]);
          } else {
            console.error("Backend login failed with status", response.status);
            notify("We couldn't finish signing you in because Rigby's servers didn't respond. Please try again in a moment.");
            try { await signOut(auth); } catch (_) { /* ignore */ }
          }
        } catch (error) {
          console.error("Backend authentication error:", error);
          notify("We couldn't reach Rigby's servers. Please check your connection and try again.");
          try { await signOut(auth); } catch (_) { /* ignore */ }
        }
      } else {
        setFirebaseUser(null);
      }
    });

    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ===================================================== */
  /* ================== LOGIN HANDLERS =================== */
  /* ===================================================== */
  const submitForgotPassword = async (email) => {
    try {
      const outcome = await requestPasswordResetDetailed(email);
      // Our own limit: say when they can ask again, and do not sidestep it with Firebase's email
      if (outcome.status === "rate_limited") return outcome;
      if (outcome.status !== "sent") {
        await sendPasswordResetEmail(auth, email); // fallback: Firebase's default email
      }
    } catch (error) {
      // Don't reveal whether the address has an account; only surface rate limiting
      if (error?.code === "auth/too-many-requests") return { status: "rate_limited" };
    }
    return { status: "sent" };
  };

  // Sends the verification email for a signed-in, unverified user. Resolves {status, retryAfter?}.
  const sendVerificationFor = async (user) => {
    const outcome = await requestVerificationEmailDetailed(user);
    if (outcome.status !== "unavailable") return outcome;
    try {
      await sendEmailVerification(user); // fallback: Firebase's default email
      return { status: "sent" };
    } catch (error) {
      return { status: error?.code === "auth/too-many-requests" ? "rate_limited" : "error" };
    }
  };

  // "Send the email again" from the unverified sign-in popup. Signs in again with the password just typed.
  const resendVerificationFromLogin = async (email, password) => {
    try {
      const cred = await signInWithEmailAndPassword(auth, email, password);
      if (cred.user.emailVerified) {
        // Verified in the meantime: stay signed in; the auth listener finishes the sign-in
        setAuthNotice(null);
        return { status: "verified" };
      }
      const outcome = await sendVerificationFor(cred.user);
      await signOut(auth);
      return outcome;
    } catch (error) {
      try {
        await signOut(auth);
      } catch (_) {
        // already signed out
      }
      return { status: error?.code === "auth/too-many-requests" ? "rate_limited" : "error" };
    }
  };

  const handleEmailLogin = async () => {
    setAuthLoading(true);
    try {
      const email = loginUsername.trim().toLowerCase();
      const typedPassword = loginPassword;
      const cred = await signInWithEmailAndPassword(auth, email, typedPassword);
      if (!cred.user.emailVerified) {
        const outcome = await sendVerificationFor(cred.user);
        await signOut(auth);
        const resent = outcome.status === "sent";
        setAuthNotice({
          title: "Please verify your email",
          body: resent
            ? `We just sent a new verification link to ${email}. Open it, then sign in again. Check your spam folder if you don't see it.`
            : "Your email isn't verified yet. We've sent several links recently, so please check your inbox and spam folder.",
          // When we are over the limit, the button stays locked until the exact time the backend gave us
          resend: {
            onResend: () => resendVerificationFromLogin(email, typedPassword),
            seconds: resent ? undefined : outcome.retryAfter,
          },
        });
      }
      // Verified users are completed by the Firebase auth listener
    } catch (error) {
      const code = error?.code;
      if (code === "auth/invalid-credential" || code === "auth/wrong-password" || code === "auth/user-not-found") {
        setAuthNotice({
          title: "Incorrect email or password",
          body: "Check your details and try again. If you signed up with Google, use Continue with Google.",
          secondary: { label: "Forgot password?", onClick: () => setForgotOpen(true) },
        });
      } else if (code === "auth/password-does-not-meet-requirements") {
        setAuthNotice({
          title: "Please choose a new password",
          body: "Your password no longer meets our security rules.",
          primary: { label: "Reset my password", onClick: () => setForgotOpen(true) },
        });
      } else if (code === "auth/too-many-requests") {
        setAuthNotice({ title: "Too many attempts", body: "Please wait a few minutes and try again." });
      } else {
        setAuthNotice({ title: "Couldn't sign in", body: "Something went wrong. Please try again." });
      }
    } finally {
      setAuthLoading(false);
    }
  };

  const handleLogin = async () => {
    if (!loginUsername || !loginPassword) {
      setAuthNotice({ title: "Enter your details", body: "Please enter both your email and your password." });
      return;
    }

    // Email addresses sign in through Firebase; plain usernames are legacy accounts
    if (loginUsername.includes("@")) {
      await handleEmailLogin();
      return;
    }

    setAuthLoading(true);
    try {
      const response = await fetch(`${API_BASE}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          username: loginUsername,
          password: loginPassword,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        const firstName = data.user?.first_name || loginUsername.split('@')[0];
        setUserName(firstName);
        setUserFirstName(firstName);
        setIsGuest(false); // User is authenticated, not a guest
        setIsAuthenticated(true); // Mark as authenticated
        setIsRegister(false); // Close register modal if open
        
        // Store user ID for user-specific tasks
        if (data.user?.id) {
          setUserId(data.user.id);
        }
        
        // Load avatar from backend if available
        if (data.user?.avatar_url) {
          setUserAvatar(data.user.avatar_url);
        }
        
        // Clear login form
        setLoginUsername("");
        setLoginPassword("");
        
        // Fetch total message count after login
        fetchTotalMessageCount();
        
        // Load conversations
        loadConversations();
        
        // Rigby welcome message
        setMessages([
          {
            sender: "bot",
            text: greeting(firstName),
            timestamp: new Date().toISOString(),
          }
        ]);
      } else {
        // Handle error response
        let errorMessage = "Login failed";
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorMessage;
        } catch (e) {
          errorMessage = `Login failed: ${response.status} ${response.statusText}`;
        }
        notify(errorMessage);
      }
    } catch (error) {
      console.error("Login error:", error);
      if (error.name === "TypeError" && error.message.includes("fetch")) {
        notify("Cannot connect to server. Please check if the backend is running.");
      } else {
        notify(`Connection error: ${error.message}. Please try again.`);
      }
    } finally {
      setAuthLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setAuthLoading(true);
    googleSignInInProgressRef.current = true;
    try {
      // Sign out first so we always get a fresh token (fixes "invalid token" when signing in again right after logout)
      try {
        await signOut(auth);
      } catch (_) {
        // Ignore if already signed out
      }
      console.log("Starting Google sign-in...");
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      console.log("Google sign-in successful:", user.email);
      
      // Reload user to ensure we have the latest profile data including photoURL
      try {
        await user.reload();
        console.log("User profile reloaded, photoURL:", user.photoURL);
      } catch (reloadError) {
        console.warn("Could not reload user profile:", reloadError);
        // Continue anyway, use existing user data
      }
      
      // Get a fresh ID token (force refresh to avoid cached/expired token)
      const idToken = await user.getIdToken(true);
      
      // Send to backend (with timeout so we don't hang forever)
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 25000);
      const response = await fetch(`${API_BASE}/auth/firebase-login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          email: user.email,
          displayName: user.displayName || "",
          photoURL: user.photoURL || "",
          idToken: idToken
        }),
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      if (response.status === 403 && response.headers.get("X-Requires-FERPA-Consent") === "true") {
        setShowFerpaConsentForGoogle(true);
        setPendingGoogleUser(user);
        setAuthLoading(false);
        return;
      }
      if (response.ok) {
        const data = await response.json();
        const firstName = data.user?.first_name || user.displayName?.split(' ')[0] || "friend";
        const lastName = data.user?.last_name || user.displayName?.split(' ').slice(1).join(' ') || "";
        // Use full name for display, or first name if last name is not available
        const fullName = lastName ? `${firstName} ${lastName}` : firstName;
        
        setUserName(fullName);
        setUserFirstName(firstName);
        
        // Handle avatar: use Firebase photoURL if available
        // Check photoURL from multiple sources to be sure
        const photoURL = user.photoURL || result.user?.photoURL || user.providerData?.[0]?.photoURL;
        console.log("Google sign-in - photoURL from user.photoURL:", user.photoURL);
        console.log("Google sign-in - photoURL from result.user:", result.user?.photoURL);
        console.log("Google sign-in - photoURL from providerData:", user.providerData?.[0]?.photoURL);
        console.log("Google sign-in - Final photoURL:", photoURL);
        console.log("Google sign-in - photoURL type:", typeof photoURL);
        console.log("Google sign-in - Full user object keys:", Object.keys(user));
        
        setIsGuest(false); // Google sign-in users are authenticated, not guests
        setIsAuthenticated(true); // Mark as authenticated
        
        // Store user ID for user-specific tasks
        if (data.user?.id) {
          setUserId(data.user.id);
        }
        
        // Load avatar from backend if available (backend avatar takes precedence over Google photoURL)
        if (data.user?.avatar_url) {
          console.log("✅ Setting avatar from backend:", data.user.avatar_url);
          setUserAvatar(data.user.avatar_url);
        } else if (photoURL && typeof photoURL === 'string' && photoURL.trim() !== "" && photoURL !== "null" && photoURL !== "undefined") {
          // Fall back to Google photoURL if no backend avatar
          const trimmedURL = photoURL.trim();
          console.log("✅ Setting avatar from Google photoURL:", trimmedURL);
          setUserAvatar(trimmedURL);
        } else {
          console.warn("⚠️ No valid photoURL available, showing initials");
          setUserAvatar(null); // Explicitly set to null to show initials
          // Clear any cached avatar from localStorage
          localStorage.removeItem("rigby-user-avatar");
        }
        
        // Fetch total message count
        fetchTotalMessageCount();
        
        // Load conversations
        loadConversations();
        
        // Rigby welcome message
        setMessages([
          {
            sender: "bot",
            text: greeting(firstName),
            timestamp: new Date().toISOString(),
          }
        ]);
        
        console.log("✅ Google sign-in complete!");
      } else {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.detail || "Backend authentication failed");
      }
    } catch (error) {
      console.error("Google sign-in error details:", error);
      console.error("Error code:", error.code);
      console.error("Error message:", error.message);
      if (error.name === "AbortError") {
        notify("The server took too long to respond. Check that the backend is running and that BACKEND_URL on the frontend points to it (e.g. https://your-backend.up.railway.app).");
        return;
      }
      if (error.message?.includes("Firebase token") || error.message?.includes("401")) {
        console.error("Tip: Ensure the backend Firebase service account JSON is from the same project as the frontend (rigby-71325) and that the backend container clock is correct.");
      }
      if (error.code === 'auth/popup-closed-by-user') {
        notify("Sign-in popup was closed. Please try again.");
      } else if (error.code === 'auth/unauthorized-domain') {
        notify("Firebase Error: localhost is not authorized.\n\nPlease add 'localhost' and '127.0.0.1' to Firebase Console → Authentication → Settings → Authorized domains");
      } else if (error.code === 'auth/popup-blocked') {
        notify("Pop-up blocked by browser.\n\nPlease allow pop-ups for this site and try again.");
      } else if (error.code === 'auth/cancelled-popup-request') {
        notify("Another sign-in request is already in progress. Please wait.");
      } else if (error.code === 'auth/invalid-credential' || error.message?.includes('invalid_client') || error.message?.includes('Unauthorized')) {
        notify(
          "Firebase OAuth Configuration Error:\n\n" +
          "The Google Sign-In is not properly configured in Firebase Console.\n\n" +
          "To fix this:\n" +
          "1. Go to Firebase Console (https://console.firebase.google.com)\n" +
          "2. Select your project: rigby-f23e7\n" +
          "3. Go to Authentication → Sign-in method\n" +
          "4. Enable 'Google' sign-in provider\n" +
          "5. Add your project's support email\n" +
          "6. Save the changes\n\n" +
          "If the issue persists, check Google Cloud Console → APIs & Services → Credentials"
        );
      } else {
        notify(`Google sign-in failed: ${error.message || "Unknown error"}\n\nError code: ${error.code || "N/A"}\n\nPlease check the browser console for details.`);
      }
    } finally {
      googleSignInInProgressRef.current = false;
      setAuthLoading(false);
    }
  };

  const doFirebaseConsentLogin = async (firebaseUser) => {
    setAuthLoading(true);
    try {
      const idToken = await firebaseUser.getIdToken(true);
      const response = await fetch(`${API_BASE}/auth/firebase-consent`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          idToken,
          displayName: firebaseUser.displayName || "",
          photoURL: firebaseUser.photoURL || firebaseUser.providerData?.[0]?.photoURL || "",
        }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || "Could not complete sign-in");
      }
      const data = await response.json();
      const firstName = data.user?.first_name || firebaseUser.displayName?.split(" ")[0] || "friend";
      const lastName = data.user?.last_name || firebaseUser.displayName?.split(" ").slice(1).join(" ") || "";
      const fullName = lastName ? `${firstName} ${lastName}` : firstName;
      setUserName(fullName);
      setUserFirstName(firstName);
      setIsGuest(false);
      setIsAuthenticated(true);
      if (data.user?.id) setUserId(data.user.id);
      const photoURL = firebaseUser.photoURL || firebaseUser.providerData?.[0]?.photoURL;
      if (data.user?.avatar_url) {
        setUserAvatar(data.user.avatar_url);
      } else if (photoURL && typeof photoURL === "string" && photoURL.trim() !== "") {
        setUserAvatar(photoURL.trim());
      } else {
        setUserAvatar(null);
        localStorage.removeItem("rigby-user-avatar");
      }
      setShowFerpaConsentForGoogle(false);
      setPendingGoogleUser(null);
      fetchTotalMessageCount();
      loadConversations();
      setMessages([{
        sender: "bot",
        text: greeting(firstName),
        timestamp: new Date().toISOString(),
      }]);
    } catch (error) {
      console.error("FERPA consent / firebase-consent error:", error);
      notify(`Sign-in could not be completed: ${error.message || "Unknown error"}`);
    } finally {
      setAuthLoading(false);
    }
  };

  const handleFerpaConsentAccept = async () => {
    if (!pendingGoogleUser) return;
    await doFirebaseConsentLogin(pendingGoogleUser);
  };

  const handleFerpaConsentCancel = async () => {
    try {
      await signOut(auth);
    } catch (e) {
      console.warn("Sign out on cancel:", e);
    }
    setShowFerpaConsentForGoogle(false);
    setPendingGoogleUser(null);
  };

  useEffect(() => {
    if (!isGuest || !guestExpiresAt) {
      setGuestSecondsLeft(null);
      return undefined;
    }
    const tick = () => {
      const left = Math.max(0, Math.round((guestExpiresAt - Date.now()) / 1000));
      setGuestSecondsLeft(left);
      if (left === 0) setGuestEnded((e) => e || "time");
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [isGuest, guestExpiresAt]);

  const handleCreateAccountFromGuest = async () => {
    await handleLogout();
    setGuestEnded(null);
    setGuestExpiresAt(null);
    setIsRegister(true);
  };

  const handleGuest = async () => {
    setAuthLoading(true);
    try {
      const response = await fetch(`${API_BASE}/auth/temp-session`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const info = await response.json().catch(() => null);
        setGuestExpiresAt(Date.now() + (info?.expires_in_minutes || 10) * 60 * 1000);
        setGuestEnded(null);
        // Response is ok, proceed with guest session setup
        // Always set guest username to just "Guest", not the full guest ID
        setUserName("Guest");
        setUserFirstName("Guest");
        setIsGuest(true);
        
        // Rigby welcome message for guest
        setMessages([
          {
            sender: "bot",
            text: greeting(""),
            timestamp: new Date().toISOString(),
          }
        ]);
      } else {
        let errorMessage = "Failed to start guest session";
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || `${errorMessage}: ${response.statusText}`;
        } catch (e) {
          errorMessage = `${errorMessage}: ${response.status} ${response.statusText}`;
        }
        console.error("Guest session failed:", response.status, errorMessage);
        setGuestNotice(
          response.status === 429
            ? { title: "Guest demo limit reached", body: errorMessage, signup: true }
            : { title: "We couldn't start the demo", body: errorMessage, signup: false }
        );
      }
    } catch (error) {
      console.error("Guest session error:", error);
      setGuestNotice({
        title: "Connection problem",
        body: "We couldn't reach Rigby's servers. Please check your connection and try again.",
        signup: false,
      });
    } finally {
      setAuthLoading(false);
    }
  };

  const handleLogout = async () => {
    // Prevent multiple simultaneous logout calls
    if (authLoading) return;
    setAuthLoading(true);

    // Sign out of Firebase
    try {
      await signOut(auth);
    } catch (error) {
      console.error("Firebase sign-out error:", error);
    }

    // Sign out of backend (works for both authenticated and guest users)
    try {
      const response = await fetch(`${API_BASE}/auth/logout`, {
        method: "POST",
        credentials: "include",
      });
      // Logout succeeds even if there's no token (for guest users or already logged out)
      if (!response.ok && response.status !== 401) {
        console.warn("Logout response not OK:", response.status);
      }
    } catch (error) {
      // Logout should still proceed even if backend call fails
      console.error("Logout error:", error);
    } finally {
      setAuthLoading(false);
    }

    // Reset all authentication and user state
    setShowLockIn(false);
    setIsGuest(false);
    setIsAuthenticated(false); // Reset authentication state
    setIsRegister(false); // Reset register state
    setMessages([]);
    setCurrentConversationId(null);
    setShowTaskPage(false);
    setShowProfilePage(false);
    setUserName("Guest User");
    setUserFirstName("");
    setUserId(null); // Reset user ID on logout
    setFirebaseUser(null);
    setUserAvatar(null);
    
    // Clear login form
    setLoginUsername("");
    setLoginPassword("");
  };

  /* ===================================================== */
  /* ============== AVATAR UPLOAD ======================== */
  /* ===================================================== */
  const handleAvatarUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      notify('Please select an image file');
      return;
    }
    
    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      notify('Image size must be less than 10MB');
      return;
    }
    
    const reader = new FileReader();
    
    // Add error handler for FileReader
    reader.onerror = () => {
      notify("Error reading file. Please try again.");
      setAuthLoading(false);
    };
    
    reader.onloadend = async () => {
      try {
        const dataURL = reader.result;
        
        // Validate data URL format
        if (!dataURL || typeof dataURL !== 'string' || !dataURL.startsWith('data:image/')) {
          notify("Invalid image file. Please select a valid image.");
          return;
        }
        
        // Update local state immediately for better UX
        setUserAvatar(dataURL);
        
        // Only authenticated users can save to backend
        if (isAuthenticated && !isGuest) {
          try {
            const response = await fetch(`${API_BASE}/auth/update-avatar`, {
              method: "PUT",
              headers: { "Content-Type": "application/json" },
              credentials: "include",
              body: JSON.stringify({ avatar_url: dataURL }),
            });
            
            if (response.ok) {
              const data = await response.json();
              console.log("Avatar saved to backend:", data);
              // Avatar is already set in state, backend confirmed it's saved
            } else {
              const errorData = await response.json().catch(() => ({ detail: "Failed to save avatar" }));
              console.error("Failed to save avatar:", errorData);
              notify(`Failed to save avatar: ${errorData.detail || "Unknown error"}`);
              // Keep the local avatar even if backend save failed
            }
          } catch (error) {
            console.error("Error saving avatar:", error);
            notify("Error saving avatar. It will only be saved locally.");
            // Keep the local avatar even if backend save failed
          }
        } else {
          // Guest users - avatar only saved locally (in state)
          console.log("Guest user - avatar saved locally only");
        }
      } catch (error) {
        console.error("Error processing avatar:", error);
        notify("Error processing image. Please try again.");
      }
    };
    reader.readAsDataURL(file);
  };

  /* ===================================================== */
  /* ============== TASK HANDLERS ======================== */
  /* ===================================================== */
  const addTask = () => {
    const trimmedTask = newTask.trim();
    if (trimmedTask) {
      // Validate task length (max 500 characters)
      if (trimmedTask.length > 500) {
        notify("Task text is too long. Maximum 500 characters allowed.");
        return;
      }
      setTasks([...tasks, { id: Date.now(), text: trimmedTask, completed: false }]);
      setNewTask("");
    }
  };

  const toggleTask = (id) => {
    setTasks((prev) => prev.map((task) => (task.id === id ? { ...task, completed: !task.completed } : task)));
  };

  const editTask = (id, text) => {
    const trimmed = String(text || "").trim();
    if (!trimmed) return;
    if (trimmed.length > MAX_TASK_LENGTH) {
      notify(`Task text is too long. Maximum ${MAX_TASK_LENGTH} characters allowed.`);
      return;
    }
    setTasks((prev) => prev.map((task) => (task.id === id ? { ...task, text: trimmed } : task)));
  };

  const deleteTask = (id) => {
    setTasks((prev) => prev.filter((task) => task.id !== id));
  };

  // Opens the chat with the task already typed in. The student reads it and presses send.
  const breakDownTask = (text) => {
    setInput(breakDownMessage(text));
    setShowTaskPage(false);
  };

  /* ===================================================== */
  /* ============== FILE UPLOAD HANDLERS ================= */
  /* ===================================================== */
  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    const newFiles = files.map((file) => ({
      name: file.name,
      size: (file.size / 1024).toFixed(2) + " KB",
      type: file.type,
      file: file,
    }));
    setUploadedFiles([...uploadedFiles, ...newFiles]);
    setShowUploadMenu(false);
  };

  const removeFile = (index) => {
    setUploadedFiles(uploadedFiles.filter((_, i) => i !== index));
  };

  /* ===================================================== */
  /* ================== CHAT HANDLERS ==================== */
  /* ===================================================== */
  const sendMessage = async (messageText = input) => {
    const message = String(messageText || '').trim();
    if (!message.trim() && uploadedFiles.length === 0) return;

    let filesWithContent = [];
    
    if (uploadedFiles.length > 0) {
      try {
        filesWithContent = await Promise.all(
          uploadedFiles.map(async (fileObj) => {
            const reader = new FileReader();
            return new Promise((resolve, reject) => {
              reader.onload = () => {
                const base64String = reader.result.split(',')[1];
                resolve({
                  name: fileObj.name,
                  size: fileObj.size,
                  type: fileObj.type,
                  content: base64String,
                });
              };
              reader.onerror = reject;
              reader.readAsDataURL(fileObj.file);
            });
          })
        );
      } catch (error) {
        console.error("Error converting files:", error);
        notify("Error processing file. Please try again.");
        return;
      }
    }

    const newMessages = [
      ...messages,
      {
        sender: "user",
        text: typeof message === "string" ? message : "",
        files: uploadedFiles.length > 0 ? uploadedFiles.map(f => ({ name: f?.name, size: f?.size, type: f?.type })) : null,
      },
    ];
    setMessages(newMessages);
    setInput("");
    setUploadedFiles([]);
    setIsTyping(true);

    setTimeout(() => {
      if (chatBoxRef.current) {
        chatBoxRef.current.scrollTo({
          top: chatBoxRef.current.scrollHeight,
          behavior: "smooth",
        });
      }
    }, 100);

    try {
      const response = await fetch(`${API_BASE}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          message: message,
          conversation_id: currentConversationId,
          files: filesWithContent.length > 0 ? filesWithContent : null,
        }),
      });

      if (isGuest && (response.status === 401 ||
          (response.status === 403 && response.headers.get("X-Guest-Limit") === "messages"))) {
        setIsTyping(false);
        setGuestEnded(response.status === 401 ? "time" : "messages");
        return;
      }

      if (response.status === 401) {
        setIsTyping(false);
        notify("Session expired. Please log in again.");
        handleLogout();
        return;
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        let errorMessage = "Sorry, something went wrong 😞";
        
        if (errorData && typeof errorData === 'object') {
          if (errorData.detail) {
            errorMessage = typeof errorData.detail === 'string' 
              ? errorData.detail 
              : JSON.stringify(errorData.detail);
          } else if (errorData.message) {
            errorMessage = errorData.message;
          }
        }
        
        setIsTyping(false);
        setMessages([
          ...newMessages,
          { sender: "bot", text: `Error: ${errorMessage}` },
        ]);
        return;
      }

      const data = await response.json();
      
      if (data.conversation_id) {
        setCurrentConversationId(data.conversation_id);
      }
      
      await new Promise((r) => setTimeout(r, 800));
      setIsTyping(false);
      
      let botMessage = "I received your message.";
      if (typeof data === 'string') {
        botMessage = data;
      } else if (data.response && typeof data.response === 'string') {
        botMessage = data.response;
      } else if (data.message && typeof data.message === 'string') {
        botMessage = data.message;
      }
      
      setMessages([...newMessages, { sender: "bot", text: botMessage }]);

      if (data.is_crisis) {
        setShowCrisisModal(true);
      }

      // Refresh total message count after receiving bot response
      fetchTotalMessageCount();
      
      // Refresh conversations list if sidebar is open
      if (showConversations) {
        loadConversations();
      }
      
    } catch (error) {
      setIsTyping(false);
      console.error("Chat error:", error);
      
      setMessages([
        ...newMessages,
        { sender: "bot", text: `Connection error: ${error.message || "Couldn't connect to server"}` },
      ]);
    }
  };

  // A mood tap shows the student's tap and Rigby's fixed opener on screen only.
  // Nothing is sent to the backend and nothing is stored.
  const handleLocalExchange = (userText, botText, chips) => {
    setMessages((prev) => {
      const hasBot = prev.some((m) => m.sender === "bot");
      const base = hasBot ? prev : [{ sender: "bot", text: greeting(userFirstName) }, ...prev];
      return [...base, { sender: "user", text: userText }, { sender: "bot", text: botText, chips }];
    });
  };

  const clearChat = async () => {
    if (
      await confirmDialog({
        title: "Clear this chat?",
        body: "This clears the conversation on your screen.",
        confirmLabel: "Clear chat",
        danger: true,
      })
    ) {
      const firstName = userFirstName || "friend";
      setMessages([
        { 
          sender: "bot", 
          text: greeting(firstName) 
        }
      ]);
      setCurrentConversationId(null);
    }
  };

  const startNewChat = () => {
    const firstName = userFirstName || "friend";
    setMessages([
      { 
        sender: "bot", 
        text: greeting(firstName) 
      }
    ]);
    setCurrentConversationId(null);
    setShowTaskPage(false);
    setShowProfilePage(false);
  };

  /* ===================================================== */
  /* ================== FERPA MODAL ====================== */
  /* ===================================================== */
  // Rendered by calling them (not as <Component />) so the dialog keeps its focus when the app re-renders.
  const FerpaModal = () => <FerpaInfoModal onClose={() => setShowFerpaModal(false)} />;

  const FerpaConsentForGoogleModal = () => (
    <FerpaConsentModal
      copy={consentCopy(pendingGoogleUser)}
      busy={authLoading}
      onCancel={handleFerpaConsentCancel}
      onAccept={handleFerpaConsentAccept}
    />
  );

  /* ===================================================== */
  /* =================== PROFILE PAGE ==================== */
  /* ===================================================== */
  // GUESTS CANNOT ACCESS PROFILE PAGE - Block completely
  // If guest somehow accesses profile page, useEffect above will redirect them
  // Only show profile page for non-guest users
  if (isAuthenticated && showProfilePage) {
    const fbUser = auth.currentUser;
    return (
      <div className={`app-container ${darkMode ? "dark-mode" : "light-mode"}`}>
        <ProfilePage
          userName={userName}
          avatarUrl={userAvatar}
          signInMethod={signInMethodOf(fbUser)}
          memberSince={memberSinceLabel(fbUser && fbUser.metadata && fbUser.metadata.creationTime)}
          messageCount={totalMessageCount}
          darkMode={darkMode}
          avatarInputRef={avatarInputRef}
          onAvatarChange={handleAvatarUpload}
          onBack={() => setShowProfilePage(false)}
          onToggleDark={() => setDarkMode(!darkMode)}
          onChangePassword={handleChangePassword}
          onExport={exportUserData}
          onDeleteAllData={deleteUserData}
          onOpenTerms={() => setShowFerpaModal(true)}
          onDeleteAccount={deleteAccountNow}
          onLogout={handleLogout}
        />
        {showFerpaModal && FerpaModal()}
      </div>
    );
  }

  /* ===================================================== */
  /* =================== TASK PAGE ======================= */
  /* ===================================================== */
  if (isAuthenticated && !isGuest && showTaskPage) {
    return (
      <div className={`app-container ${darkMode ? "dark-mode" : "light-mode"}`}>
        <TasksScreen
          tasks={tasks}
          newTask={newTask}
          onNewTaskChange={setNewTask}
          onAdd={addTask}
          onToggle={toggleTask}
          onEdit={editTask}
          onDelete={deleteTask}
          onBreakDown={breakDownTask}
          onAskRigby={() => setShowTaskPage(false)}
          onBack={() => setShowTaskPage(false)}
        />
      </div>
    );
  }

  /* ===================================================== */
  /* =================== LOCK-IN PAGE ==================== */
  /* ===================================================== */
  if (isAuthenticated && !isGuest && showLockIn) {
    return (
      <div className={`app-container ${darkMode ? "dark-mode" : "light-mode"}`}>
        <LockInScreen
          tasks={tasks}
          onCompleteTask={(id) => {
            const t = tasks.find((x) => x.id === id);
            if (t && !t.completed) toggleTask(id);
          }}
          onBack={() => setShowLockIn(false)}
        />
      </div>
    );
  }

  /* ===================================================== */
  /* =================== CHATBOT PAGE ==================== */
  /* ===================================================== */
  if (isGuest || isAuthenticated) {
    return (
      <div className={`app-container ${darkMode ? "dark-mode" : "light-mode"}`}>
        {isGuest && guestEnded && (
          <GuestEnded reason={guestEnded} onCreateAccount={handleCreateAccountFromGuest} onBack={handleLogout} />
        )}
        {showTermsModal && <TermsModal onClose={() => setShowTermsModal(false)} />}

        {showCrisisModal && <CrisisModal onClose={() => setShowCrisisModal(false)} />}

        <ChatScreen
          darkMode={darkMode}
          isGuest={isGuest}
          guestSecondsLeft={guestSecondsLeft}
          userName={userName}
          userFirstName={userFirstName}
          avatarUrl={userAvatar}
          messages={messages}
          isTyping={isTyping}
          input={input}
          onInputChange={setInput}
          onSend={sendMessage}
          onLocalExchange={handleLocalExchange}
          onOpenHistory={() => {
            setShowConversations(!showConversations);
            if (!showConversations) loadConversations();
          }}
          onNewChat={startNewChat}
          onOpenTasks={() => setShowTaskPage(true)}
          onOpenLockIn={() => setShowLockIn(true)}
          onOpenProfile={() => {
            setShowProfilePage(true);
            fetchTotalMessageCount();
          }}
          onClearChat={clearChat}
          onLogout={handleLogout}
          onCreateAccount={handleCreateAccountFromGuest}
          onToggleDark={() => setDarkMode(!darkMode)}
          onOpenTerms={() => setShowTermsModal(true)}
          scrollRef={chatBoxRef}
          fileInputRef={fileInputRef}
          onFileChange={handleFileUpload}
          onToggleUpload={() => setShowUploadMenu(!showUploadMenu)}
          attachments={
            <>
{uploadedFiles.length > 0 && (
            <div className="file-preview-container">
              {uploadedFiles.map((file, index) => (
                <div key={index} className="file-preview-item">
                  <div className="file-info">
                    <div className="file-name">{typeof file?.name === "string" ? file.name : (file?.name != null ? String(file.name) : "File")}</div>
                    <div className="file-size">{typeof file?.size === "string" ? file.size : (file?.size != null ? String(file.size) : "")}</div>
                  </div>
                  <button
                    className="remove-file-btn"
                    onClick={() => removeFile(index)}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}

          {showUploadMenu && (
            <div className="upload-menu">
              <button
                className="upload-menu-item"
                onClick={() => fileInputRef.current?.click()}
              >
                Upload files
              </button>
            </div>
          )}
            </>
          }
        />
        <HistoryView
          open={showConversations}
          onClose={() => setShowConversations(false)}
          conversations={conversations}
          loading={conversationsLoading}
          currentId={currentConversationId}
          onSelect={loadConversation}
          onNewChat={() => {
            setShowConversations(false);
            startNewChat();
          }}
          onDeleteConversation={deleteConversationById}
        />
      </div>
    );
  }

  /* ===================================================== */
  /* =================== LOGIN PAGE ====================== */
  /* ===================================================== */
  if (authAction && !isAuthenticated) {
    return (
      <AuthActionPage
        mode={authAction.mode}
        oobCode={authAction.oobCode}
        onDone={() => {
          window.history.replaceState({}, "", window.location.pathname);
          setAuthAction(null);
          setIsRegister(false);
        }}
      />
    );
  }

  if (!isAuthenticated && !isRegister) {
    return (
      <div className="rigby-login fade-in">
        <div className="rigby-login-brand">
          <div className="rigby-login-brand-bg" />
          <div className="rigby-login-brand-top">
            <img src={rigbyHead} alt="Rigby" className="rigby-login-mark" />
            <span className="rigby-login-wordmark">My Emotional Support Bot</span>
          </div>

          <div className="rigby-login-brand-mid">
            <div className="rigby-login-badge">
              <span className="rigby-login-badge-dot" />
              Welcome back
            </div>
            <h1 className="rigby-login-headline">
              Real support for <em>real student life.</em>
            </h1>
            <p className="rigby-login-lede">
              A quiet place to land between classes. Tell Rigby how you're arriving and pick up wherever you left off.
            </p>

            <div className="rigby-login-preview">
              <div className="rigby-login-preview-row">
                <img src={rigbyHead} alt="" className="rigby-login-preview-avatar" />
                <div className="rigby-login-preview-bubble">Hey, glad you're back. How's today going so far?</div>
              </div>
              <div className="rigby-login-preview-typing">
                <span /><span /><span />
              </div>
            </div>
          </div>

          <div className="rigby-login-trust">
            {["FERPA Compliant", "CASEL Aligned SEL", "Crisis Detection"].map((label) => (
              <div key={label} className="rigby-login-trust-item">
                <span className="rigby-login-trust-icon"><IconCheck /></span>
                {label}
              </div>
            ))}
          </div>
        </div>

        <div className="rigby-login-form-panel">
          <div className="rigby-login-form-card">
            <h2 className="rigby-login-form-title">
              Hey, it's <em>Rigby.</em>
            </h2>
            <p className="rigby-login-form-subtitle">
              New here?{" "}
              <span className="rigby-login-link" onClick={() => setIsRegister(true)}>Create an account</span>
            </p>

            <button className="rigby-login-google-btn" onClick={handleGoogleSignIn} disabled={authLoading}>
              <IconGoogle /> {authLoading ? "Signing in..." : "Continue with Google"}
            </button>

            <div className="rigby-login-divider"><span>OR</span></div>

            <div className="rigby-login-field">
              <label>Email</label>
              <input
                type="text"
                placeholder="you@university.edu"
                value={loginUsername}
                onChange={(e) => setLoginUsername(e.target.value)}
              />
            </div>

            <div className="rigby-login-field">
              <div className="rigby-login-field-row">
                <label>Password</label>
                <span className="rigby-login-link" onClick={() => setForgotOpen(true)}>Forgot password?</span>
              </div>
              <div className="rigby-login-password-wrap">
                <input
                  type={showLoginPassword ? "text" : "password"}
                  placeholder="••••••••••"
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                />
                <button
                  type="button"
                  className="rigby-login-eye-btn"
                  onClick={() => setShowLoginPassword(!showLoginPassword)}
                  title={showLoginPassword ? "Hide password" : "Show password"}
                >
                  <IconEye open={!showLoginPassword} />
                </button>
              </div>
            </div>

            <button className="rigby-login-submit-btn" onClick={handleLogin} disabled={authLoading}>
              {authLoading ? "Signing in..." : "Sign in & check in"} <IconArrow />
            </button>

            <div className="rigby-login-guest-row">
              <span className="rigby-login-link" onClick={handleGuest}>Continue as guest</span>
              <span className="rigby-login-dot">·</span>
              <span className="rigby-login-encrypted"><IconLock /> Encrypted</span>
            </div>

            <p className="rigby-login-legal">
              By signing in you agree to our{" "}
              <button onClick={() => setShowTermsModal(true)}>Terms & Conditions</button>
              {" "}and{" "}
              <button onClick={() => setShowFerpaModal(true)}>FERPA Privacy Policy</button>.
            </p>
          </div>
        </div>

        {showTermsModal && <TermsModal onClose={() => setShowTermsModal(false)} />}

        {showFerpaModal && FerpaModal()}
        {showFerpaConsentForGoogle && FerpaConsentForGoogleModal()}
        <ForgotPasswordDialog
          open={forgotOpen}
          initialEmail={loginUsername}
          onSubmit={submitForgotPassword}
          onClose={() => setForgotOpen(false)}
        />
        <NoticeDialog notice={authNotice} onClose={() => setAuthNotice(null)} />
        {guestNotice && (
          <MessageModal
            title={guestNotice.title}
            body={guestNotice.body}
            onClose={() => setGuestNotice(null)}
            actions={
              guestNotice.signup ? (
                <>
                  <button type="button" className="rg-modal-btn rg-modal-btn--ghost" onClick={() => setGuestNotice(null)}>
                    Sign in instead
                  </button>
                  <button
                    type="button"
                    data-autofocus
                    className="rg-modal-btn rg-modal-btn--primary"
                    onClick={() => { setGuestNotice(null); setIsRegister(true); }}
                  >
                    Create free account
                  </button>
                </>
              ) : (
                <button type="button" data-autofocus className="rg-modal-btn rg-modal-btn--primary" onClick={() => setGuestNotice(null)}>
                  OK
                </button>
              )
            }
          />
        )}
      </div>
    );
  }

  /* ===================================================== */
  /* ================== REGISTER PAGE ==================== */
  /* ===================================================== */
  if (!isAuthenticated && isRegister) {
    return (
      <>
        <SignupPage
          authLoading={authLoading}
          googleIcon={<IconGoogle />}
          onGoogle={handleGoogleSignIn}
          onBackToLogin={() => setIsRegister(false)}
          onShowTerms={() => setShowTermsModal(true)}
          onShowFerpa={() => setShowFerpaModal(true)}
        />
        {showFerpaModal && FerpaModal()}
        {showFerpaConsentForGoogle && FerpaConsentForGoogleModal()}
        <ForgotPasswordDialog
          open={forgotOpen}
          initialEmail={loginUsername}
          onSubmit={submitForgotPassword}
          onClose={() => setForgotOpen(false)}
        />
        <NoticeDialog notice={authNotice} onClose={() => setAuthNotice(null)} />
        {guestNotice && (
          <MessageModal
            title={guestNotice.title}
            body={guestNotice.body}
            onClose={() => setGuestNotice(null)}
            actions={
              guestNotice.signup ? (
                <>
                  <button type="button" className="rg-modal-btn rg-modal-btn--ghost" onClick={() => setGuestNotice(null)}>
                    Sign in instead
                  </button>
                  <button
                    type="button"
                    data-autofocus
                    className="rg-modal-btn rg-modal-btn--primary"
                    onClick={() => { setGuestNotice(null); setIsRegister(true); }}
                  >
                    Create free account
                  </button>
                </>
              ) : (
                <button type="button" data-autofocus className="rg-modal-btn rg-modal-btn--primary" onClick={() => setGuestNotice(null)}>
                  OK
                </button>
              )
            }
          />
        )}
      {showTermsModal && <TermsModal onClose={() => setShowTermsModal(false)} />}
      </>
    );
  }

  // Fallback: if somehow we reach here without being authenticated or on register page, show login
  return (
    <div className="login-container fade-in">
      <div className="login-left">
        <img src="/assets/Rigby-bot.gif" alt="Rigby Bot" className="login-video" />
      </div>
      <div className="login-right">
        <div className="login-card">
          <h1 className="title">Welcome to Rigby 🤖</h1>
          <p className="subtitle">Your Emotional Support Chatbot</p>
          <p style={{ textAlign: "center", color: "#666", marginTop: "1rem" }}>
            Please log in to continue
          </p>
        </div>
      </div>
    </div>
  );
}

export default function AppWithDialogs() {
  return (
    <DialogProvider>
      <App />
    </DialogProvider>
  );
}